import { useState, useEffect, useContext, useCallback } from "react";
import { getCollections } from "../services/profileApi.jsx";
import { getAdminProfiles, getProfileById } from "../services/associationApi.jsx";
import { ContextApp } from "../utils/Context.jsx";
import { ConfigContext } from "../utils/Config.jsx";

interface ProfileData {
  profileName: string;
  collections: string[];
  includeHistory: boolean;
  systemInstruction: string;
  modelId: string;
  numSearchDocuments: number | null;
  numChatMessages: number | null;
  temperature: number | null;
}

interface Profile {
  profile_id: string;
  backend_config: {
    collections: string[];
    prompt: Array<[string, string]>;
    model_id?: string;
    num_search_documents?: number;
    num_chat_messages?: number;
    temperature?: number;
  };
}

interface PaginationState {
  currentPage: number;
  hasNextPage: boolean;
  lastKey: string | null;
  isLoading: boolean;
}

export const useProfileData = (isOpen: boolean, editMode: boolean = false) => {
  const { collections: contextCollections } = useContext(ContextApp);
  const configContext = useContext(ConfigContext);

  const getDefaultCollection = useCallback((): string => {
    if (configContext && configContext.config && configContext.config.DEFAULT_COLLECTION) {
      return configContext.config.DEFAULT_COLLECTION;
    }
    return "DEFAULT_COLLECTION";
  }, [configContext]);

  const getInitialProfileData = useCallback((): ProfileData => {
    return {
      profileName: "",
      collections: [],
      includeHistory: false,
      systemInstruction: "",
      modelId: "",
      numSearchDocuments: null,
      numChatMessages: null,
      temperature: null,
    };
  }, []);

  const [profileData, setProfileData] = useState<ProfileData>(getInitialProfileData());
  const [availableCollections, setAvailableCollections] = useState<string[]>([]);
  const [existingProfiles, setExistingProfiles] = useState<Profile[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [pagination, setPagination] = useState<PaginationState>({
    currentPage: 1,
    hasNextPage: false,
    lastKey: null,
    isLoading: false
  });

  const [paginationTokens, setPaginationTokens] = useState<string[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProfiles, setTotalProfiles] = useState(0);
  const [searchProfileId, setSearchProfileId] = useState<string>("");

  const isDefaultCollection = useCallback((collection: string): boolean => {
    const defaultCollection = getDefaultCollection();
    return collection === defaultCollection;
  }, [getDefaultCollection]);

  const getUniqueCollections = useCallback((): string[] => {
    return [...new Set(profileData.collections)];
  }, [profileData.collections]);

  const loadCollections = useCallback(async (): Promise<void> => {
    try {
      setIsLoading(true);
      const apiCollections = await getCollections();
      
      const allCollections = [...new Set([
        ...contextCollections || [],
        ...apiCollections || []
      ])];
      
      setAvailableCollections(allCollections);

      if (editMode) {
        const defaultCollection = getDefaultCollection();
        
        setProfileData(prev => {
          if (allCollections.includes(defaultCollection) && !prev.collections.includes(defaultCollection)) {
            return {
              ...prev,
              collections: [...prev.collections, defaultCollection]
            };
          }
          return prev;
        });
      }
    } catch (error) {
      const contextCols = contextCollections || [];
      setAvailableCollections(contextCols);
      
      if (editMode) {
        const defaultCollection = getDefaultCollection();
        
        setProfileData(prev => {
          if (contextCols.includes(defaultCollection) && !prev.collections.includes(defaultCollection)) {
            return {
              ...prev,
              collections: [...prev.collections, defaultCollection]
            };
          }
          return prev;
        });
      }
    } finally {
      setIsLoading(false);
    }
  }, [contextCollections, getDefaultCollection, editMode]);

  const fetchProfiles = useCallback(async (page: number = 1, limit: number = 10): Promise<void> => {
    try {
      setPagination(prev => ({ ...prev, isLoading: true }));
      
      let data;
      if (searchProfileId.trim()) {
        const profile = await getProfileById(searchProfileId.trim());
        if (profile) {
          data = { profiles: [profile] };
        } else {
          data = { profiles: [] };
        }
      } else {
        const apiParams: any = {
          limit
        };

        if (page > 1 && paginationTokens[page - 2]) {
          apiParams.start_key = paginationTokens[page - 2];
        }

        data = await getAdminProfiles(limit, apiParams.start_key);
      }
      
      if (data && Array.isArray(data.profiles)) {
        setExistingProfiles(data.profiles);
        
        const hasMorePages = !!data.last_key;
        
        setPagination({
          currentPage: page,
          hasNextPage: hasMorePages,
          lastKey: data.last_key ?? null,
          isLoading: false
        });

        if (hasMorePages && data.last_key) {
          const newTokens = [...paginationTokens];
          newTokens[page - 1] = data.last_key;
          setPaginationTokens(newTokens);
          setTotalPages(page + 1);
        } else {
          setTotalPages(page);
        }

        if (page === 1) {
          setTotalProfiles(data.profiles.length);
        } else {
          setTotalProfiles((page - 1) * limit + data.profiles.length);
        }
      } else {
        setExistingProfiles([]);
        setPagination(prev => ({ ...prev, isLoading: false }));
        setTotalProfiles(0);
        setTotalPages(1);
      }
    } catch (error) {
      setPagination(prev => ({ ...prev, isLoading: false }));
      setExistingProfiles([]);
      setTotalProfiles(0);
      setTotalPages(1);
    }
  }, [paginationTokens, searchProfileId]);

  const handlePageChange = useCallback((newPage: number): void => {
    if (newPage > totalPages || newPage < 1) {
      return;
    }

    if (newPage > 1 && !paginationTokens[newPage - 2]) {
      return;
    }

    fetchProfiles(newPage, 10);
  }, [totalPages, paginationTokens, fetchProfiles]);

  const handleCollectionToggle = useCallback((collection: string): void => {
    setProfileData(prev => {
      if (prev.collections.includes(collection)) {
        return {
          ...prev,
          collections: prev.collections.filter(c => c !== collection)
        };
      } else {
        return {
          ...prev,
          collections: [...prev.collections, collection]
        };
      }
    });
  }, []);

  const updateProfileData = useCallback((updates: Partial<ProfileData>): void => {
    setProfileData(prev => ({ ...prev, ...updates }));
  }, []);

  const resetProfileData = useCallback((): void => {
    setProfileData(getInitialProfileData());
  }, [getInitialProfileData]);

  const loadProfileForEdit = useCallback((profile: Profile): void => {
    const historyPrompt = profile.backend_config.prompt.find(
      ([type, content]) => type === "placeholder" && content === "{history}"
    );
    
    const systemPrompt = profile.backend_config.prompt.find(
      ([type, content]) => type === "system" && content !== "{context}"
    );
    
    setProfileData({
      profileName: profile.profile_id,
      collections: [...profile.backend_config.collections],
      includeHistory: !!historyPrompt,
      systemInstruction: systemPrompt ? systemPrompt[1] : "",
      modelId: profile.backend_config.model_id || "",
      numSearchDocuments: profile.backend_config.num_search_documents !== undefined ? profile.backend_config.num_search_documents : null,
      numChatMessages: profile.backend_config.num_chat_messages !== undefined ? profile.backend_config.num_chat_messages : null,
      temperature: profile.backend_config.temperature !== undefined && profile.backend_config.temperature !== null ? Number(profile.backend_config.temperature) : null,
    });
  }, []);

  const formatProfileForAPI = useCallback((sanitizedProfileId?: string): any => {
    const profileId = sanitizedProfileId || profileData.profileName
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9_-]/g, "_");

    const formattedPrompts: Array<[string, string]> = [];

    if (profileData.includeHistory) {
      formattedPrompts.push(["placeholder", "{history}"]);
    }

    formattedPrompts.push(["system", "{context}"]);

    if (profileData.systemInstruction.trim()) {
      formattedPrompts.push(["system", profileData.systemInstruction.trim()]);
    }

    formattedPrompts.push(["human", "{question}"]);

    const uniqueCollections = [...new Set(profileData.collections)];

    const backendConfig: any = {
      collections: uniqueCollections,
      prompt: formattedPrompts,
    };

    if (profileData.modelId && profileData.modelId.trim()) {
      backendConfig.model_id = profileData.modelId;
    } else {
      backendConfig.model_id = "";
    }

    if (profileData.numSearchDocuments !== null) {
      backendConfig.num_search_documents = profileData.numSearchDocuments;
    } else {
      backendConfig.num_search_documents = null;
    }

    if (profileData.numChatMessages !== null) {
      backendConfig.num_chat_messages = profileData.numChatMessages;
    } else {
      backendConfig.num_chat_messages = null;
    }

    if (profileData.temperature !== null && profileData.temperature !== undefined) {
      backendConfig.temperature = profileData.temperature;
    } else {
      backendConfig.temperature = null;
    }

    const result = {
      profile_id: profileId,
      backend_config: backendConfig,
    };
    return result;
  }, [profileData]);

  const resetPagination = useCallback((): void => {
    setPagination({
      currentPage: 1,
      hasNextPage: false,
      lastKey: null,
      isLoading: false
    });
    setPaginationTokens([]);
    setTotalPages(1);
    setTotalProfiles(0);
  }, []);

  const handleSearchByProfile = useCallback(async (profileId: string): Promise<void> => {
    setSearchProfileId(profileId);
    
    setPagination({
      currentPage: 1,
      hasNextPage: false,
      lastKey: null,
      isLoading: true
    });
    setPaginationTokens([]);
    setTotalPages(1);
    setTotalProfiles(0);
    
    try {
      const profile = await getProfileById(profileId.trim());
      let data;
      if (profile) {
        data = { profiles: [profile] };
      } else {
        data = { profiles: [] };
      }
      
      setExistingProfiles(data.profiles || []);
      setTotalProfiles(data.profiles?.length || 0);
      setPagination({
        currentPage: 1,
        hasNextPage: false,
        lastKey: null,
        isLoading: false
      });
      setTotalPages(1);
    } catch (error) {
      setPagination(prev => ({ ...prev, isLoading: false }));
      setExistingProfiles([]);
      setTotalProfiles(0);
    }
  }, []);

  const clearSearch = useCallback(async (): Promise<void> => {
    setSearchProfileId("");
    
    setPagination({
      currentPage: 1,
      hasNextPage: false,
      lastKey: null,
      isLoading: true
    });
    setPaginationTokens([]);
    setTotalPages(1);
    setTotalProfiles(0);
    
    try {
      const data = await getAdminProfiles(10);
      
      setExistingProfiles(data.profiles || []);
      setTotalProfiles(data.profiles?.length || 0);
      
      const hasMorePages = !!data.last_key;
      setPagination({
        currentPage: 1,
        hasNextPage: hasMorePages,
        lastKey: data.last_key ?? null,
        isLoading: false
      });
      
      if (hasMorePages && data.last_key) {
        setTotalPages(2);
        setPaginationTokens([data.last_key]);
      } else {
        setTotalPages(1);
      }
    } catch (error) {
      setPagination(prev => ({ ...prev, isLoading: false }));
      setExistingProfiles([]);
      setTotalProfiles(0);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadCollections();
      fetchProfiles(1, 10);
    } else {
      resetPagination();
    }
  }, [isOpen]);

  return {
    profileData,
    availableCollections,
    existingProfiles,
    isLoading,
    pagination,
    totalPages,
    totalProfiles,
    searchProfileId,
    
    setProfileData,
    updateProfileData,
    resetProfileData,
    loadProfileForEdit,
    handleCollectionToggle,
    loadCollections,
    fetchProfiles,
    handlePageChange,
    resetPagination,
    handleSearchByProfile,
    clearSearch,
    
    getDefaultCollection,
    isDefaultCollection,
    getUniqueCollections,
    formatProfileForAPI
  };
};